import React from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ValueSkeleton } from '@/components/ui/ValueSkeleton';

interface ViewTabProps {
  viewId: string;
  name: string;
  isActive: boolean;
  /** Auto-generated per-CP view. Used only to render an explanatory tooltip. */
  isAutoGenerated?: boolean;
  /** Undefined while the counts load: a placeholder of about a number's width; null when
   *  they failed to: a dash. */
  tradeCount: number | null | undefined;
  onActivate: () => void;
  /** A tab opened from the search closes; the others have no X. */
  onClose?: () => void;
}

export const ViewTab: React.FC<ViewTabProps> = ({
  viewId,
  name,
  isActive,
  isAutoGenerated,
  tradeCount,
  onActivate,
  onClose,
}) => {
  // A read-only tab: a single activate button wrapped in the pill. The shared
  // background/border lives on the wrapper so the pill reads as one tab visually.
  return (
    <div
      data-view-id={viewId}
      className={clsx(
        'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium motion-safe:transition-colors select-none whitespace-nowrap',
        isActive
          ? 'bg-white text-slate-900 font-semibold border-t border-x border-slate-300 rounded-t-lg -mb-[1px] relative z-10'
          : 'text-slate-500 bg-transparent rounded-md hover:bg-slate-200 hover:text-slate-800'
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={isActive}
        title={isAutoGenerated ? 'Auto view — per-counterparty tab' : undefined}
        className="flex items-center gap-1.5 cursor-pointer rounded focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        onClick={onActivate}
        // Backspace too: the Mac keyboard's delete key sends it.
        aria-keyshortcuts={onClose ? 'Delete Backspace' : undefined}
        onKeyDown={
          onClose
            ? (e) => {
                // Not on auto-repeat (a held key would close the next tab too) or a chord.
                if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  onClose();
                }
              }
            : undefined
        }
      >
        {/* View name */}
        <span className="truncate max-w-[160px]">{name}</span>

        {/* Trade count */}
        {tradeCount === undefined ? (
          <ValueSkeleton className="h-3 w-5" />
        ) : (
          <span
            className={clsx(
              'text-xs tabular-nums flex-shrink-0',
              isActive ? 'text-slate-500' : 'text-slate-400'
            )}
          >
            {tradeCount ?? '—'}
          </span>
        )}
      </button>
      {/* Mouse only: a tablist may own only tabs, so the keyboard closes the tab with Delete. */}
      {onClose && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          title={`Close ${name}`}
          className="-mr-1 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          onClick={onClose}
        >
          <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
